import { API_TYPES, get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";
import { z } from "zod";

import { type PlacementDetail } from "./useGetPlacement";

interface GetInterviewAvailabilityParams {
  workplaceId: string;
}

interface GetInterviewAvailabilityQueryParams {
  filter?: {
    workerId?: string;
    canScheduleInterviews?: boolean;
    placementId?: string;
    excludedInterviewId?: string;
    start?: {
      gte?: Date;
    };
  };
}

const interviewAvailabilitySchema = z.object({
  start: z.string(),
  end: z.string(),
});
export type AvailabilityTimeslot = z.infer<typeof interviewAvailabilitySchema>;

export type TransformedAvailabilityTimeslot = { start: Date; end: Date; isBooked?: boolean };

export const getAvailabilityResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      type: z.literal(API_TYPES.interviewAvailability),
      attributes: interviewAvailabilitySchema,
    })
  ),
  meta: z
    .object({
      bookedInterviews: z.array(interviewAvailabilitySchema).optional(),
    })
    .optional(),
  links: z.object({
    next: z.string().optional(),
    prev: z.string().optional(),
  }),
});

export type GetAvailabilityResponse = z.infer<typeof getAvailabilityResponseSchema>;

export function transformInterviewAvailabilityData(
  interviewAvailability: GetAvailabilityResponse | undefined,
  workerTimezone: string,
  placementDetail: PlacementDetail
) {
  if (!interviewAvailability) {
    return [];
  }

  const conflictingTimeslots = placementDetail.interviews
    .filter((interview) => interview.attributes.status === InterviewStatus.BOOKED)
    .map((interview) => {
      return {
        start: interview.attributes.start,
        end: interview.attributes.end,
      };
    });

  const availableSlots: TransformedAvailabilityTimeslot[] = interviewAvailability.data
    .filter(
      (availability) =>
        !conflictingTimeslots.some(
          (timeslot) =>
            timeslot.start === availability.attributes.start &&
            timeslot.end === availability.attributes.end
        )
    )
    .map((availability) => ({
      start: utcToZonedTime(availability.attributes.start, workerTimezone),
      end: utcToZonedTime(availability.attributes.end, workerTimezone),
    }));

  const bookedInterviews = interviewAvailability.meta?.bookedInterviews ?? [];
  if (bookedInterviews.length === 0) {
    return availableSlots;
  }

  const bookedSlots: TransformedAvailabilityTimeslot[] = bookedInterviews.map((slot) => ({
    start: utcToZonedTime(slot.start, workerTimezone),
    end: utcToZonedTime(slot.end, workerTimezone),
    isBooked: true,
  }));

  return [...availableSlots, ...bookedSlots].sort((a, b) => a.start.getTime() - b.start.getTime());
}

export type InterviewAvailability = ReturnType<typeof transformInterviewAvailabilityData>;

function fetchInterviewAvailabilityUrl(workplaceId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/interviews/availability`;
}

export function useGetInterviewAvailability(
  params: GetInterviewAvailabilityParams,
  queryParams?: GetInterviewAvailabilityQueryParams,
  options?: UseQueryOptions<GetAvailabilityResponse>
): UseQueryResult<GetAvailabilityResponse> {
  const { workplaceId } = params;
  const { filter } = queryParams ?? {};

  return useQuery({
    queryKey: [
      `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/interviews/availability`,
      filter,
    ],
    queryFn: async () => {
      if (!filter?.canScheduleInterviews) {
        return {
          data: [],
          links: {
            next: undefined,
            prev: undefined,
          },
        };
      }

      const response = await get({
        url: fetchInterviewAvailabilityUrl(workplaceId),
        responseSchema: getAvailabilityResponseSchema,
        queryParams: {
          filter: {
            ...(filter.workerId && { workerId: filter.workerId }),
            ...(filter.placementId && { placementId: filter.placementId }),
            ...(filter.excludedInterviewId && {
              interviewId: { nin: filter.excludedInterviewId },
            }),
            ...(filter.start && { start: filter.start }),
          },
        },
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_INTERVIEW_AVAILABILITY_FAILURE,
    },
    enabled: options?.enabled ?? false,
    useErrorBoundary: false,
  });
}
